import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authUtils } from '../utils/auth';
import { profileApi } from '../services/profileApi';
import { jobLoadService } from '../services/jobLoadService';
import { ONBOARDING_COMPLETED_KEY } from './onboarding/types';
import { ProfileResponse } from '../types/profile';
import JobLoadingModal from './JobLoadingModal';
import CVUploadModal from './CVUploadModal';

type FlowState = 'idle' | 'loading_job' | 'needs_cv' | 'error';

function checkHasProfile(response: ProfileResponse): boolean {
  const content = response?.data?.content;
  if (!content) return false;
  const name = (content as any)?.personal_details?.name;
  const exp = (content as any)?.professional_experience;
  const edu = (content as any)?.education;
  return (
    (typeof name === 'string' && name.trim() !== '') ||
    (Array.isArray(exp) && exp.length > 0) ||
    (Array.isArray(edu) && edu.length > 0)
  );
}

const PendingJobHandler: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [flowState, setFlowState] = useState<FlowState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [pendingCipher, setPendingCipher] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const cipher = authUtils.getPendingJobCipher();
    if (!cipher) return;
    setPendingCipher(cipher);
    runJobFlow(cipher);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const runJobFlow = async (cipher: string) => {
    // Suppress onboarding for users arriving via job link
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');

    setFlowState('loading_job');
    try {
      const profileResponse = await profileApi.loadProfile();
      const hasProfile = checkHasProfile(profileResponse);
      if (!hasProfile) {
        setFlowState('needs_cv');
        return;
      }
    } catch {
      // If profile load fails with auth error, interceptor will redirect
      return;
    }
    await executeJobLoad(cipher);
  };

  const executeJobLoad = async (cipher: string) => {
    setFlowState('loading_job');

    const loadResult = await jobLoadService.loadJob(cipher);
    if (!loadResult.success) {
      setErrorMessage(loadResult.error!);
      setFlowState('error');
      return;
    }

    const createResult = await jobLoadService.createJobCard(loadResult.data!);
    if (!createResult.success) {
      setErrorMessage(createResult.error!);
      setFlowState('error');
      return;
    }

    authUtils.removePendingJobCipher();
    if (createResult.jobUrl) {
      sessionStorage.setItem('open_job_url', createResult.jobUrl);
    }
    setFlowState('idle');
    navigate('/jobtracking');
  };

  const handleCVSuccess = async () => {
    const cipher = pendingCipher || authUtils.getPendingJobCipher();
    if (!cipher) {
      setFlowState('idle');
      return;
    }
    await executeJobLoad(cipher);
  };

  const handleSkip = () => {
    authUtils.removePendingJobCipher();
    setFlowState('idle');
  };

  const handleDismissError = () => {
    authUtils.removePendingJobCipher();
    setFlowState('idle');
  };

  if (flowState === 'idle') return null;

  if (flowState === 'needs_cv') {
    return <CVUploadModal onSuccess={handleCVSuccess} onSkip={handleSkip} />;
  }

  return (
    <JobLoadingModal
      state={flowState === 'error' ? 'error' : 'loading'}
      errorMessage={errorMessage}
      onDismiss={handleDismissError}
    />
  );
};

export default PendingJobHandler;
